/**
 * The open times piece's behavior half: reads the services and the chosen
 * one's next openings in the visitor's zone, labels the days, opens a time.
 * The list runs most booked first and pages perView at a time. Draws nothing.
 */
import { useMemo, useState } from 'react';
import type { SiteNextOpenings, SiteServiceSummaryDto } from '@telify/shared';

import { useWebSession } from '../../api/session';
import { mostBookedFirst, useNextOpenings, usePopularServiceIds, useSiteServices } from '../../api/site';
import { usePieceEmit, usePieceOptions, type PieceInput, type PieceRuntime } from '../../contract/runtime';
import { getClientTimezone, slotTimeLabels, zoneName } from '../../format';
import { usePageMover } from '../../pageMover';
import { wholeCount } from '../../wholeCount';
import {
  contract,
  type OpenTimesActions,
  type OpenTimesDay,
  type OpenTimesEvents,
  type OpenTimesState,
} from './contract';

export type OpenTimesRuntime = PieceRuntime<typeof contract, OpenTimesState, OpenTimesActions>;

const MAX_DAYS = 4;
const MAX_TIMES = 4;

/** YYYY-MM-DD of the day `offset` days from today, in `tz`. */
function dayIn(tz: string, offset: number): string {
  const today = new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(
    new Date(),
  );
  const d = new Date(`${today}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + offset);
  return d.toISOString().slice(0, 10);
}

/** A calendar day (YYYY-MM-DD) formatted on its own, with no zone shift. */
function formatDate(date: string, opts: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat('en-US', { ...opts, timeZone: 'UTC' }).format(new Date(`${date}T12:00:00Z`));
}

function timeHref(service: SiteServiceSummaryDto, startsAt: string): string {
  return `${service.path}?start=${encodeURIComponent(startsAt)}`;
}

function buildDays(service: SiteServiceSummaryDto, openings: SiteNextOpenings | undefined, tz: string): OpenTimesDay[] {
  if (!openings) return [];
  const today = dayIn(tz, 0);
  const tomorrow = dayIn(tz, 1);
  return openings.days
    .filter((d) => d.times.length > 0)
    .slice(0, MAX_DAYS)
    .map((d) => ({
      date: d.date,
      name:
        d.date === today
          ? 'Today'
          : d.date === tomorrow
            ? 'Tomorrow'
            : `${formatDate(d.date, { weekday: 'short' })} ${formatDate(d.date, { day: 'numeric' })}`,
      dateLabel: formatDate(d.date, { weekday: 'short', month: 'short', day: 'numeric' }),
      times: d.times.slice(0, MAX_TIMES).map((t, i, shown) => ({
        startsAt: t.startsAt,
        label: slotTimeLabels(shown.map((x) => x.startsAt), tz)[i]!,
        href: timeHref(service, t.startsAt),
      })),
    }));
}

export function useOpenTimes(input: PieceInput<OpenTimesEvents> = {}): OpenTimesRuntime {
  const options = usePieceOptions(contract, input.options);
  const session = useWebSession().status;
  const emit = usePieceEmit(input.onEvent);
  const navigate = usePageMover();
  const tz = useMemo(() => getClientTimezone(), []);
  const servicesQuery = useSiteServices();
  const popularQuery = usePopularServiceIds();
  const services = useMemo(() => servicesQuery.data ?? [], [servicesQuery.data]);
  // Held until the ranking answers, so the list never reorders under the visitor; a failed ranking keeps the studio order.
  const ordered = useMemo(
    () => (popularQuery.isPending ? [] : mostBookedFirst(services, popularQuery.data ?? [])),
    [services, popularQuery.isPending, popularQuery.data],
  );
  const [chosenId, setChosenId] = useState<string | null>(null);
  const [wantedPage, setWantedPage] = useState(0);

  const perView = wholeCount(options.perView);
  const pageCount = Math.max(1, Math.ceil(ordered.length / perView));
  const page = Math.min(wantedPage, pageCount - 1);
  const shown = useMemo(() => ordered.slice(page * perView, (page + 1) * perView), [ordered, page, perView]);

  // The chosen service while it is still listed, else the most booked.
  const selected = ordered.find((s) => s.id === chosenId) ?? ordered[0] ?? null;
  const openingsQuery = useNextOpenings(selected?.id, tz);

  const days = useMemo(
    () => (selected ? buildDays(selected, openingsQuery.data, tz) : []),
    [selected, openingsQuery.data, tz],
  );
  const status: OpenTimesState['status'] = servicesQuery.isError
    ? 'error'
    : servicesQuery.isPending || popularQuery.isPending
      ? 'loading'
      : 'ready';
  const timesStatus: OpenTimesState['timesStatus'] = openingsQuery.isError
    ? 'error'
    : openingsQuery.isPending
      ? 'loading'
      : 'ready';

  const actions = useMemo<OpenTimesActions>(
    () => ({
      selectService: (serviceId) => {
        if (!services.some((s) => s.id === serviceId)) return;
        setChosenId(serviceId);
        emit('serviceSelected', { serviceId });
      },
      openTime: (startsAt) => {
        if (!selected) return;
        emit('timeOpened', { serviceId: selected.id, startsAt });
        navigate(timeHref(selected, startsAt));
      },
      openService: () => {
        if (!selected) return;
        emit('serviceOpened', { serviceId: selected.id });
        navigate(selected.path);
      },
      previous: () => {
        if (page === 0) return;
        setWantedPage(page - 1);
        emit('paged', { page: page - 1 });
      },
      next: () => {
        if (page >= pageCount - 1) return;
        setWantedPage(page + 1);
        emit('paged', { page: page + 1 });
      },
    }),
    [services, selected, page, pageCount, emit, navigate],
  );

  return {
    state: {
      status,
      services,
      selected,
      shown,
      page,
      pageCount,
      firstShown: shown.length > 0 ? page * perView + 1 : 0,
      canPrevious: page > 0,
      canNext: page < pageCount - 1,
      timesStatus,
      days,
      timezone: tz,
      zoneName: zoneName(tz),
      signedIn: session === 'signedIn',
    },
    actions,
    options,
  };
}
